import { useEffect, useMemo, useState } from "react";
import { Search, Users as UsersIcon, Smartphone, Apple, Globe, HelpCircle } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState, TableSkeleton } from "@/components/EmptyState";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";

function PlatformBadge({ platform, compact = false }) {
  const p = (platform || "").toLowerCase();
  const config = {
    ios:     { label: "iOS",     Icon: Apple,       cls: "bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900" },
    android: { label: "Android", Icon: Smartphone,  cls: "bg-emerald-600 text-white" },
    web:     { label: "Web",     Icon: Globe,       cls: "bg-sky-600 text-white" },
  }[p] || { label: "Unknown", Icon: HelpCircle, cls: "bg-muted text-muted-foreground" };
  const { Icon } = config;
  return (
    <span
      className={
        "inline-flex items-center gap-1 rounded-md font-medium " +
        config.cls +
        (compact ? " px-1.5 py-0 text-[10px]" : " px-2 py-0.5 text-xs")
      }
    >
      <Icon className={compact ? "size-2.5" : "size-3"} />
      {config.label}
    </span>
  );
}

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [platformFilter, setPlatformFilter] = useState("all");

  useEffect(() => {
    api.get("/users")
      .then((r) => setUsers(r.data))
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    return users.filter((u) => {
      if (statusFilter !== "all" && (u.subscription_status || "free") !== statusFilter) return false;
      if (platformFilter !== "all" && (u.platform || "unknown") !== platformFilter) return false;
      if (!query) return true;
      const q = query.toLowerCase();
      return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
    });
  }, [users, query, statusFilter, platformFilter]);

  return (
    <>
      <PageHeader
        title="Users"
        description="People who signed up for The Greater Life. Search by name or email; filter by subscription status."
      />

      <Card className="border-border/70 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3 p-4 border-b border-border">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              data-testid="users-search"
              placeholder="Search by name or email…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex gap-1.5 flex-wrap" data-testid="users-status-filter">
            {[
              { key: "all", label: "All" },
              { key: "free", label: "Free" },
              { key: "active", label: "Active" },
              { key: "expired", label: "Expired" },
            ].map((opt) => (
              <button
                key={opt.key}
                type="button"
                data-testid={`filter-${opt.key}`}
                onClick={() => setStatusFilter(opt.key)}
                className={
                  "px-3 py-1.5 text-xs font-medium rounded-md border transition-colors " +
                  (statusFilter === opt.key
                    ? "bg-foreground text-background border-foreground"
                    : "bg-background border-border text-muted-foreground hover:text-foreground")
                }
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="flex gap-1.5 flex-wrap" data-testid="users-platform-filter">
            {[
              { key: "all", label: "Any platform" },
              { key: "ios", label: "iOS" },
              { key: "android", label: "Android" },
              { key: "web", label: "Web" },
              { key: "unknown", label: "Unknown" },
            ].map((opt) => (
              <button
                key={opt.key}
                type="button"
                data-testid={`platform-filter-${opt.key}`}
                onClick={() => setPlatformFilter(opt.key)}
                className={
                  "px-3 py-1.5 text-xs font-medium rounded-md border transition-colors " +
                  (platformFilter === opt.key
                    ? "bg-foreground text-background border-foreground"
                    : "bg-background border-border text-muted-foreground hover:text-foreground")
                }
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="p-4">
            <TableSkeleton rows={6} cols={4} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-4">
            <EmptyState
              icon={UsersIcon}
              title={users.length === 0 ? "No users yet" : "No matching users"}
              description={
                users.length === 0
                  ? "When people sign up via the mobile app, you'll see them here."
                  : "Try a different search term or clear the filter."
              }
            />
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-muted-foreground text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left font-medium px-4 py-3">User</th>
                    <th className="text-left font-medium px-4 py-3">Country</th>
                    <th className="text-left font-medium px-4 py-3">Platform</th>
                    <th className="text-left font-medium px-4 py-3">Subscription</th>
                    <th className="text-left font-medium px-4 py-3">Last login</th>
                    <th className="text-left font-medium px-4 py-3">Joined</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.map((u) => (
                    <tr key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="size-9 rounded-full bg-gradient-to-br from-primary to-accent text-white flex items-center justify-center text-xs font-semibold shrink-0">
                            {(u.name || u.email || "?").slice(0, 1).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium truncate">{u.name || "—"}</p>
                            <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{u.country || "—"}</td>
                      <td className="px-4 py-3" data-testid={`user-platform-${u.id}`}>
                        <PlatformBadge platform={u.platform} />
                      </td>
                      <td className="px-4 py-3">
                        <Badge
                          variant={u.subscription_status === "active" ? "default" : "secondary"}
                          className="capitalize"
                        >
                          {u.subscription_status || "free"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">
                        {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <ul className="md:hidden divide-y divide-border">
              {filtered.map((u) => (
                <li key={u.id} className="p-4 flex items-center gap-3">
                  <div className="size-10 rounded-full bg-gradient-to-br from-primary to-accent text-white flex items-center justify-center text-sm font-semibold shrink-0">
                    {(u.name || u.email || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{u.name || "—"}</p>
                    <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      <Badge
                        variant={u.subscription_status === "active" ? "default" : "secondary"}
                        className="capitalize text-[10px] py-0"
                      >
                        {u.subscription_status || "free"}
                      </Badge>
                      <PlatformBadge platform={u.platform} compact />
                      {u.country && <span className="text-[11px] text-muted-foreground">{u.country}</span>}
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <div className="px-4 py-3 border-t border-border text-xs text-muted-foreground flex justify-between">
              <span>
                Showing {filtered.length} of {users.length}
              </span>
            </div>
          </>
        )}
      </Card>
    </>
  );
}
